<template>
  <div class="h-vset">
    <div class="h-vset__content">
      <div class="h-vset__head">
        <h1 class="h-vset__title">Vendor settings</h1>
        <p class="h-vset__sub">{{ loading ? 'Loading…' : vat.vendor_name }}</p>
      </div>

      <HermesEmptyState
        v-if="loadError"
        variant="error"
        title="Couldn't load the vendor's settings"
        :message="loadError"
        @retry="load"
      />

      <section v-else-if="!loading" class="h-vset__card">
        <h2 class="h-vset__cardtitle">VAT</h2>
        <p class="h-vset__p">
          Hermes adds VAT to a price that is net, and records the VAT on every payment. A change applies to
          charges made from now on; payments already made keep the VAT they were recorded with.
        </p>

        <label class="h-vset__check">
          <input v-model="form.vat_registered" type="checkbox" />
          <span>This vendor is VAT registered</span>
        </label>

        <template v-if="form.vat_registered">
          <div class="h-vset__grid2">
            <div class="h-vset__field">
              <label class="h-vset__label" for="h-vset-number">VAT number</label>
              <input id="h-vset-number" v-model.trim="form.vat_number" class="h-vset__input" type="text" maxlength="32" placeholder="GB123456789" autocomplete="off" />
              <p v-if="fieldError('vat_number')" class="h-vset__err">{{ fieldError('vat_number') }}</p>
            </div>
            <div class="h-vset__field">
              <label class="h-vset__label" for="h-vset-rate">Standard rate (%)</label>
              <input id="h-vset-rate" v-model.trim="form.vat_rate" class="h-vset__input" type="text" inputmode="decimal" maxlength="6" />
              <p v-if="fieldError('vat_rate')" class="h-vset__err">{{ fieldError('vat_rate') }}</p>
            </div>
          </div>
          <p class="h-vset__hint">A charge may override the rate, for example a zero-rated payment request.</p>
        </template>
        <p v-else class="h-vset__hint">No VAT is charged. A net price is charged as it is.</p>

        <dl class="h-vset__facts">
          <div>
            <dt>Today</dt>
            <dd>A price of £100.00 net is charged {{ money(vat.example && vat.example.amount_cents) }}<template v-if="vat.example && vat.example.vat_cents"> (VAT {{ money(vat.example.vat_cents) }})</template>.</dd>
          </div>
        </dl>

        <p v-if="state.error && !state.field" class="h-vset__saveerr">{{ state.error }}</p>
        <p v-if="state.saved" class="h-vset__saveok">Saved.</p>

        <div class="h-vset__foot">
          <button type="button" class="h-btn h-btn--primary" :disabled="state.saving || !changed" @click="save">
            {{ state.saving ? 'Saving…' : 'Save VAT settings' }}
          </button>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";

// HermesVendorSettings — the vendor's VAT registration, number and standard rate
// (docs/vat-handling.md). Platform operators only. Reads and writes
// /admin/vendor/vat for the vendor the session resolves to (the operator's own,
// or the one they act as).
export default {
  name: 'HermesVendorSettings',
  props: {
    apiBase: { type: String, default: '/api/v1' },
    componentData: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      api: useApi(),
      loading: true,
      loadError: null,
      vat: {},
      form: { vat_registered: false, vat_number: '', vat_rate: '' },
      state: { saving: false, saved: false, error: '', field: '' },
    };
  },
  computed: {
    changed() {
      return this.form.vat_registered !== !!this.vat.vat_registered
        || (this.form.vat_number || '') !== (this.vat.vat_number || '')
        || Number(this.form.vat_rate) !== Number(this.vat.vat_rate);
    },
  },
  methods: {
    async load() {
      this.loading = true;
      this.loadError = null;
      try {
        const data = await this.api.get(`${this.apiBase}/admin/vendor/vat`);
        this.apply(data.vat || {});
      } catch (e) {
        const status = e?.response?.status;
        this.loadError = `Failed to load${status ? ` (${status})` : ''}. ${e?.response?.data?.message || e?.message || ''}`.trim();
      } finally {
        this.loading = false;
      }
    },
    apply(vat) {
      this.vat = vat;
      this.form = {
        vat_registered: !!vat.vat_registered,
        vat_number: vat.vat_number || '',
        vat_rate: vat.vat_rate != null ? String(Number(vat.vat_rate)) : '',
      };
    },
    fieldError(field) {
      return this.state.field === field ? this.state.error : '';
    },
    async save() {
      if (!this.changed || this.state.saving) return;
      this.state = { saving: true, saved: false, error: '', field: '' };
      const payload = { vat_registered: this.form.vat_registered };
      if (this.form.vat_registered) {
        payload.vat_number = this.form.vat_number;
        payload.vat_rate = this.form.vat_rate;
      }
      try {
        const data = await this.api.patch(`${this.apiBase}/admin/vendor/vat`, payload);
        this.apply(data.vat || {});
        this.state = { saving: false, saved: true, error: '', field: '' };
      } catch (e) {
        const status = e?.response?.status;
        this.state = {
          saving: false,
          saved: false,
          error: e?.response?.data?.message || `Could not save${status ? ` (${status})` : ''}.`,
          field: e?.response?.data?.field || '',
        };
      }
    },
    money(pence) {
      if (pence == null) return '—';
      return `£${(Number(pence) / 100).toFixed(2)}`;
    },
  },
  mounted() {
    this.load();
  },
};
</script>

<style scoped>
.h-vset { font-family: var(--h-font-body, system-ui); color: var(--h-text-primary, #0F172A); background: var(--h-bg-content, #F8FAFC); min-height: 100vh; }
.h-vset__content { padding: 26px 32px 64px; max-width: 760px; width: 100%; }
.h-vset__head { margin-bottom: 22px; }
.h-vset__title { font-size: 24px; font-weight: 600; letter-spacing: -0.02em; margin: 0 0 4px; }
.h-vset__sub { font-size: 14px; color: var(--h-text-secondary, #64748B); margin: 0; }

.h-vset__card { background: var(--h-bg-card, #fff); border: 1px solid var(--h-border, #E2E8F0); border-radius: var(--h-radius-lg, 10px); box-shadow: var(--h-shadow-card); padding: 22px 24px; margin-bottom: 18px; }
.h-vset__cardtitle { font-size: 16px; font-weight: 600; margin: 0 0 8px; }
.h-vset__p { font-size: 13.5px; line-height: 1.55; color: var(--h-text-secondary, #64748B); margin: 0 0 16px; }

.h-vset__check { display: inline-flex; align-items: center; gap: 8px; font-size: 14px; font-weight: 500; margin-bottom: 16px; cursor: pointer; }
.h-vset__check input { width: 16px; height: 16px; accent-color: var(--h-accent, #0EA5E9); }

.h-vset__grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
.h-vset__field { margin-bottom: 12px; }
.h-vset__label { display: block; font-size: 13px; font-weight: 500; margin-bottom: 6px; }
.h-vset__hint { font-size: 12px; color: var(--h-text-tertiary, #94A3B8); margin: 0 0 4px; line-height: 1.5; }
.h-vset__err { font-size: 12.5px; color: var(--h-danger-text, #B91C1C); margin: 6px 0 0; }

.h-vset__input { width: 100%; font: inherit; font-size: 14px; background: var(--h-bg-card, #fff); border: 1px solid var(--h-border-strong, #CBD5E1); color: var(--h-text-primary, #0F172A); border-radius: var(--h-radius, 6px); padding: 9px 12px; box-sizing: border-box; }
.h-vset__input:focus { outline: none; border-color: var(--h-accent, #0EA5E9); box-shadow: var(--h-focus-ring-soft, 0 0 0 3px rgba(14,165,233,.15)); }

.h-vset__facts { margin: 14px 0 0; padding: 14px 0 0; border-top: 1px solid var(--h-border, #E2E8F0); display: grid; gap: 8px; }
.h-vset__facts > div { display: flex; gap: 12px; font-size: 13px; }
.h-vset__facts dt { width: 110px; flex: none; color: var(--h-text-tertiary, #94A3B8); }
.h-vset__facts dd { margin: 0; color: var(--h-text-primary, #0F172A); }

.h-vset__foot { display: flex; justify-content: flex-end; margin-top: 18px; }
.h-vset__saveerr { font-size: 12.5px; color: var(--h-danger-text, #B91C1C); margin-top: 14px; padding: 10px 12px; background: var(--h-danger-bg, #FEF2F2); border: 1px solid var(--h-danger-border, #FECACA); border-radius: var(--h-radius, 6px); }
.h-vset__saveok { font-size: 12.5px; color: var(--h-success-text, #047857); margin-top: 14px; padding: 10px 12px; background: var(--h-success-bg, #ECFDF5); border-radius: var(--h-radius, 6px); }

.h-btn { font: inherit; font-size: 13.5px; font-weight: 500; padding: 9px 15px; border-radius: var(--h-radius, 6px); border: 1px solid transparent; cursor: pointer; display: inline-flex; align-items: center; gap: 7px; }
.h-btn:focus-visible { outline: none; box-shadow: var(--h-focus-ring); }
.h-btn--primary { background: var(--h-accent, #0EA5E9); color: #fff; }
.h-btn--primary:hover:not(:disabled) { background: var(--h-accent-hover, #0284C7); }
.h-btn--primary:disabled { opacity: 0.6; cursor: not-allowed; }

@media (max-width: 640px) {
  .h-vset__grid2 { grid-template-columns: 1fr; }
}
</style>
